import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SecTitle from "./SecTitle";
import { Link } from "react-router-dom";
import { GoArrowRight } from "react-icons/go";
import data from "../content/Products.json";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const Services = () => {
  const products = useMemo(() => {
    const allowedTitles = new Map([
      ["Microcredit Loan"],
      ["Secured Business Loan (SBL)"],
    ]);

    return data.page
      .filter((section) => allowedTitles.has(section.title))
      .map((section) => {
        const basePath =
          section.link?.split("#")[0] || section.link || "/products";
        const sectionSlug = slugify(section.title);
        return {
          ...section,
          link: section.link || `${basePath}#${sectionSlug}`,
          displayTitle: allowedTitles.get(section.title),
          kind: "product",
          parentSlug: sectionSlug,
        };
      });
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);
  const currentProduct = products[activeIndex] || products[0];

  const detailsSource =
    currentProduct?.list && currentProduct.list.length > 0
      ? currentProduct.list
      : currentProduct?.subsections?.[0]?.list || [];

  const details = detailsSource.map((item) => ({
    title: item.title,
    values: Array.isArray(item.value) ? item.value : [item.value],
  }));

  return (
    <div className="bg-red py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <SecTitle
              title="What We Offer"
              titleColor="#fff"
              subTitle="We provide loans to individual as well as group members in the under developed regions of India for a range of income-generating activities. BSS Sonata Microcredit serves thousands of poor people across villages and urban slums in India. By providing the poors with wide range of economic tools, we are looking forward to the eradication of poverty."
              subTitleColor="#fff"
            />
          </div>
        </div>

        <div className="my-8 sm:my-12">
          <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 overflow-x-auto sm:overflow-visible pb-2 -mx-1 sm:mx-0 px-1">
            {products.map((product, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={`${product.title}-${index}`}
                  onClick={() => setActiveIndex(index)}
                  className={`rounded-2xl border px-4 py-4 sm:py-5 text-center transition-all min-w-[140px] sm:min-w-0 ${
                    isActive
                      ? "bg-white text-red border-white shadow-lg"
                      : "bg-white/10 text-white/80 border-white/30 hover:border-white/70"
                  }`}
                >
                  <p className="text-sm sm:text-lg font-semibold leading-snug text-center">
                    {product.displayTitle || product.title}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 sm:mt-10 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                <div className="grid grid-cols-1 gap-4">
                  {details.length > 0 ? (
                    currentProduct?.uiType === "table" ? (
                      /* TABLE VIEW */
                      <div className="sm:px-5 sm:py-4 bg-white rounded-2xl shadow-lg border border-white/70 overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-gray-100">
                            <tr>
                              {details.map((service, index) => (
                                <th
                                  key={index}
                                  className="px-4 py-3 font-semibold text-gray-700 border border-[#8b8b8b]"
                                >
                                  {service.title}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {details[0]?.values.map((_, rowIndex) => (
                              <tr
                                key={rowIndex}
                                className="border-b last:border-b-0"
                              >
                                {details.map((service, colIndex) => (
                                  <td
                                    key={colIndex}
                                    className="border border-[#8b8b8b] px-4 py-2 text-left "
                                  >
                                    {service.values[rowIndex] || "-"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      /* CARD VIEW */
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {details.map((service, index) => (
                          <div
                            key={`${service.title}-${index}`}
                            className="rounded-2xl bg-white shadow-lg border border-white/70 px-4 py-3 sm:px-5 sm:py-4"
                          >
                            <p className="font-semibold text-[0.7rem] tracking-[0.35em] uppercase text-gray-500 mb-2">
                              {service.title}
                            </p>

                            <ul className="text-sm sm:text-base text-gray-900 leading-relaxed space-y-1">
                              {service.values.map((value, idx) => (
                                <li key={idx}>{value}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="bg-white3 p-6 rounded-2xl text-gray-700 shadow-lg border border-white/70">
                      Detailed parameters for this product are available on the
                      product page.
                    </div>
                  )}
                </div>

                <div className="relative rounded-3xl bg-[#132347] border border-white/20 text-white shadow-2xl overflow-hidden flex">
                  <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-br from-red/60 to-transparent opacity-60" />
                  <div className="relative flex-1 p-5 sm:p-6 lg:p-8 flex flex-col gap-3 sm:gap-4 justify-center">
                    <h2 className="text-white text-lg sm:text-2xl font-semibold">
                      {currentProduct?.title}
                    </h2>
                    <p className="text-white/80 text-sm sm:text-base leading-relaxed text-justify max-w-xl mx-auto">
                      {currentProduct?.content ||
                        "No details available for this category."}
                    </p>
                    <Link
                      className="inline-flex items-center gap-2 text-white font-medium hover:underline"
                      to={currentProduct?.link || "/products"}
                    >
                      Read More
                      <GoArrowRight className="text-xl" />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Services;
